/**
 * Componente Main para OrderPreparation
 *
 * Responsabilidades:
 * - Orquestar la vista (desktop/mobile)
 * - Controlar LoadingState inicial con isInitialLoad
 * - Aplicar opacidad durante refetch (NO durante infinite scroll)
 * - Mostrar EmptyView según hasActiveFilters
 * - Mantener AG Grid siempre montado
 */
import { TotalResults } from '@shared/components/ui/TotalResults';
import { Button } from '@shared/components/ui/button';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useOrderPreparationMain } from '../../hooks/useOrderPreparationMain';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { CardList, OrderPreparationAgGridTable, OrderPreparationBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';
import { GenerateManifestModal, GenerateManifestModalRef } from '../modals/GenerateManifestModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice } from '@fortawesome/pro-regular-svg-icons';

export const Main = () => {
  const { t } = useTranslation('orderpreparationModule');

  const manifestModalRef = useRef<GenerateManifestModalRef>(null);

  // 🎯 Hook para manejar query parameters (para URL sync)
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  // 🎯 Estados locales sincronizados con URL
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Sincronizar estados locales con cambios en URL
  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  // 🎯 Handlers para sincronizar con URL - memoizados para evitar bucles
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      // Solo actualizar URL si el valor es diferente al actual
      if (queryParams.search !== searchValue) {
        updateQueryParams({ search: searchValue });
      }
    },
    [updateQueryParams, queryParams.search],
  );

  const handleFilterChangeWrapper = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      // Solo actualizar URL si los filtros son diferentes
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateQueryParams({ filters: conditions });
      }
    },
    [updateQueryParams, queryParams.filters],
  );

  // 🎯 Hook que maneja toda la lógica de negocio
  const {
    // Estados
    selectedColumns,

    // Datos
    orderpreparation,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    orderpreparationViewProps,

    // Handlers
    handleColumnsChange,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  } = useOrderPreparationMain(searchTerm, filterConditions);

  // 🎯 Obtener columnas filtradas (todas siempre visibles)
  const { columnDefs, tanStackColumns } = useAgGridColumns(selectedColumns);

  // 🎯 Columnas para el selector: usar las columnas de AG Grid convertidas a TanStack
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  const handleGenerateManifest = useCallback(() => {
    manifestModalRef.current?.open();
  }, []);

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    // Si isLoading pasa de true a false, significa que la carga inicial terminó.
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // 🎯 Función que decide qué vista renderizar (Desktop vs Mobile)
  const renderOrderPreparation = () => {
    if (isMobile) {
      return <CardList {...orderpreparationViewProps} showActions={true} selectedColumns={selectedColumns} />;
    }

    // 🎯 Usar AG Grid para la vista de escritorio
    return (
      <OrderPreparationAgGridTable
        {...orderpreparationViewProps}
        columnDefs={columnDefs} // 🎯 Pasar columnas (todas visibles)
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    );
  };

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando preparaciones...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <>
      <OrderPreparationBreadcrumb />
      <div className="px-6 pt-4">
        {/* Header con filtros y total */}
        <div className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <FiltersSection handleSearch={handleSearch} onFilterChange={handleFilterChangeWrapper} />
              <ColumnSelector
                columns={selectorColumns}
                selected={selectedColumns}
                onColumnsChange={handleColumnsChange}
                fixedColumns={['code']}
                storageKey="order-preparation-ag-grid-table"
              />
              <Button variant="outline" size="sm" onClick={handleGenerateManifest}>
                <FontAwesomeIcon icon={faFileInvoice} className="mr-2" />
                {t('actions.generateManifest', 'Generate Manifest')}
              </Button>
            </div>
            <TotalResults total={total} />
          </div>
        </div>

        {/* Estado de carga - NO se muestra porque usamos isInitialLoad */}
        {/* {isLoading && <LoadingState message={t('loading.message')} />} */}

        {/* Tabla SIEMPRE montada - Solo cambia opacidad/altura */}
        <div
          className={`transition-opacity duration-300 mb-4 mt-4 ${
            isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
          } ${!isLoading && orderpreparation.length === 0 ? 'h-0 overflow-hidden' : 'h-auto'}`}
        >
          {renderOrderPreparation()}
        </div>

        {/* Estado vacío - Solo se muestra cuando no hay datos y no está cargando */}
        {!isLoading && orderpreparation.length === 0 && (
          <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.HANDS} />
        )}

        <GenerateManifestModal ref={manifestModalRef} />
      </div>
    </>
  );
};
